import PriceChangesGlobalOrgNotice from '@/components/products/price-changes/PriceChangesGlobalOrgNotice'
import { ConfirmationDialog } from '@/components/patterns/confirmation-dialog'
import { AdminPageHeader } from '@/components/patterns/admin-page'
import CreateActionButton from '@/components/patterns/create-action-button'
import { EmptyState } from '@/components/patterns/empty-state'
import ProductCatalogNav from '@/components/products/ProductCatalogNav'
import SearchInput from '@/components/form/SearchInput'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Typography } from '@/components/ui/typography'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import useIsGlobalOrg from '@/hooks/useIsGlobalOrg'
import { usePathHelper } from '@/hooks/usePathHelper'
import { cn, getErrorMessage } from '@/lib/utils'
import {
  priceAdjustmentFilters,
  priceAdjustmentQueries,
  supportsPriceAdjustmentsForOrganizationId,
  useResetActivePriceAdjustmentsMutation,
  usePriceAdjustments,
} from '@/lib/services/price-adjustments/priceAdjustments.query'
import {
  getPriceAdjustmentCardTitle,
  getPriceAdjustmentStateMeta,
  getPriceAdjustmentScheduleSummary,
  getPriceAdjustmentSignedAmountLabel,
  getPriceAdjustmentScopeLabel,
  getPriceAdjustmentSupportingTitle,
  priceAdjustmentIsSuppressed,
  priceAdjustmentRepeatsAnnually,
} from '@/lib/services/price-adjustments/priceAdjustments.summary'
import { createFileRoute, Link, notFound } from '@tanstack/react-router'
import { ArrowDown, ArrowUp, Repeat, RotateCcw, Tags } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import {
  COLUMN_STYLES,
  getActiveBoardEffectMetaByAdjustmentId,
  adjustmentIsScheduledBoardItem,
  adjustmentIsUnscheduledBoardItem,
  adjustmentIsVisibleOnBoard,
  getPriceAdjustmentCardDomId,
  getScheduledBoardSortTimestamp,
  getRuleCountLabel,
  priceAdjustmentIsPriority,
  sortActiveBoardAdjustmentsByEffect,
  sortActiveBoardAdjustments,
  sortScheduledBoardAdjustments,
} from './-price-change-board'
import { PriceChangeStackStatus } from './-price-change-stack-status'

export const Route = createFileRoute('/_authenticated/$orgId/products/price-changes/')({
  component: PriceChangesIndexRouteComponent,
  loader: ({ context, params }) => {
    if (!context.ability.can('edit', 'products')) throw notFound()

    if (supportsPriceAdjustmentsForOrganizationId(params.orgId)) {
      void context.queryClient.ensureQueryData(
        priceAdjustmentQueries.index({
          organizationId: params.orgId,
          filters: priceAdjustmentFilters,
        })
      )
    }

    return { isReadOnly: false }
  },
})

export function PriceChangesIndexRouteComponent() {
  const isGlobalOrg = useIsGlobalOrg()

  if (isGlobalOrg) {
    return <PriceChangesIndexGlobalOrgPage />
  }

  return <PriceChangesIndexPage />
}

function PriceChangesPageIntro({ actions = null }) {
  return (
    <AdminPageHeader
      icon={Tags}
      title="Price Changes"
      description="Create scheduled price changes for holidays, busy weeks, or short-term promotions."
      actions={actions}
    />
  )
}

function PriceChangesIndexGlobalOrgPage() {
  return (
    <div className="w-full max-w-7xl space-y-4">
      <ProductCatalogNav active="price-changes" />
      <PriceChangesPageIntro />
      <PriceChangesGlobalOrgNotice />
    </div>
  )
}

export function PriceChangesIndexPage() {
  const { data: adjustments } = usePriceAdjustments()
  const path = usePathHelper()
  const resetActiveMutation = useResetActivePriceAdjustmentsMutation()
  const [search, setSearch] = useState('')
  const [highlightedAdjustmentIds, setHighlightedAdjustmentIds] = useState([])
  const [isTimelineOpen, setIsTimelineOpen] = useState(false)
  const [showStackStatus, setShowStackStatus] = useState(true)

  const boardAdjustments = adjustments.filter(adjustmentIsVisibleOnBoard)

  const filteredAdjustments = search
    ? boardAdjustments.filter((adjustment) =>
        [
          adjustment?.name,
          getPriceAdjustmentCardTitle(adjustment),
          getPriceAdjustmentSupportingTitle(adjustment),
          getPriceAdjustmentScopeLabel(adjustment),
          ...(adjustment?.collections?.map((collection) => collection?.name) ?? []),
        ]
          .filter(Boolean)
          .some((value) => value.toLowerCase().includes(search.toLowerCase()))
      )
    : boardAdjustments

  const allActiveAdjustments = sortActiveBoardAdjustments(
    adjustments.filter((adjustment) => adjustment?.status === 'active')
  )
  const snapshotScheduledAdjustments = adjustments.filter((adjustment) =>
    ['scheduled', 'draft'].includes(adjustment?.status)
  )
  const activeBoardEffectMetaByAdjustmentId =
    getActiveBoardEffectMetaByAdjustmentId(allActiveAdjustments)
  const activeAdjustments = sortActiveBoardAdjustmentsByEffect(
    filteredAdjustments.filter((adjustment) => adjustment?.status === 'active'),
    activeBoardEffectMetaByAdjustmentId
  )
  const scheduledAdjustments = sortScheduledBoardAdjustments(
    filteredAdjustments.filter(adjustmentIsScheduledBoardItem)
  )
  const unscheduledAdjustments = filteredAdjustments.filter(adjustmentIsUnscheduledBoardItem)
  const hasStackStatus =
    allActiveAdjustments.length > 0 ||
    snapshotScheduledAdjustments.some(
      (adjustment) => getScheduledBoardSortTimestamp(adjustment) !== null
    )

  const handleResetActivePriceChanges = async () => {
    try {
      await resetActiveMutation.mutateAsync()
      toast.success('Active price changes reset successfully')
    } catch (error) {
      toast.error('Reset failed', {
        description: getErrorMessage(error),
      })
    }
  }

  const handleHighlightAdjustments = (adjustmentIds) => {
    const normalizedAdjustmentIds = [...new Set(adjustmentIds.map(Number))].filter(
      (adjustmentId) => adjustmentId > 0
    )

    if (normalizedAdjustmentIds.length === 0) {
      return
    }

    setSearch('')
    setHighlightedAdjustmentIds(normalizedAdjustmentIds)

    const scrollToCard = () => {
      document
        .getElementById(getPriceAdjustmentCardDomId(normalizedAdjustmentIds[0]))
        ?.scrollIntoView?.({ block: 'center', behavior: 'smooth' })
    }

    if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(() => window.requestAnimationFrame(scrollToCard))
      return
    }

    setTimeout(scrollToCard, 0)
  }

  const resetActiveAction =
    allActiveAdjustments.length > 0 ? (
      <ConfirmationDialog
        variant="question"
        title="Reset active price changes?"
        description="This will turn off every price change that is active right now. Recurring scheduled rules will skip only this occurrence."
        confirmText="Reset active price changes"
        pendingText="Resetting..."
        onConfirm={handleResetActivePriceChanges}
        isPending={resetActiveMutation.isPending}
        trigger={
          <Button
            variant="outline"
            size={showStackStatus ? 'micro' : 'compact'}
            disabled={resetActiveMutation.isPending}
          >
            <RotateCcw className="size-4" />
            Disable All Active
          </Button>
        }
      />
    ) : null

  return (
    <div
      data-price-changes-board-page
      data-page-scroll="document"
      className="w-full max-w-7xl pb-6"
    >
      <div className="mb-4 space-y-4">
        <ProductCatalogNav active="price-changes" />
        <PriceChangesPageIntro
          actions={
            <>
              {!showStackStatus ? resetActiveAction : null}
              <CreateActionButton asChild>
                <Link {...path.priceChanges.link('create')}>Create Price Change</Link>
              </CreateActionButton>
            </>
          }
        />
      </div>

      {hasStackStatus ? (
        <div className={cn(showStackStatus && 'mb-4')}>
          <PriceChangeStackStatus
            activeAdjustments={allActiveAdjustments}
            adjustments={adjustments}
            currentAction={resetActiveAction}
            hideSnapshot={!showStackStatus}
            isTimelineOpen={isTimelineOpen}
            onHighlightAdjustments={handleHighlightAdjustments}
            onDismiss={() => setShowStackStatus(false)}
            onTimelineOpenChange={setIsTimelineOpen}
          />
        </div>
      ) : null}

      <div className="mb-4 flex flex-col gap-3 border-b border-border/70 pb-4 sm:flex-row sm:items-center sm:justify-between">
        <SearchInput
          className="w-full sm:w-xs"
          value={search}
          setValue={setSearch}
          placeholder="Search by name or collection..."
        />
        {hasStackStatus ? (
          <Button variant="outline" size="compact" onClick={() => setIsTimelineOpen(true)}>
            View Full Timeline
          </Button>
        ) : null}
      </div>

      {filteredAdjustments.length === 0 ? (
        <Card className="py-8">
          <CardContent>
            <Typography variant="supporting">
              {search ? 'No matching price changes.' : 'No price changes yet.'}
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-8 xl:grid-cols-3">
          {[
            {
              key: 'active',
              title: 'Currently Active',
              description: 'Price changes affecting products right now.',
              adjustments: activeAdjustments,
              emptyMessage: 'No active price changes.',
            },
            {
              key: 'scheduled',
              title: 'Scheduled',
              description: 'Price changes scheduled for future dates.',
              adjustments: scheduledAdjustments,
              emptyMessage: 'No scheduled price changes.',
            },
            {
              key: 'unscheduled',
              title: 'Not Active',
              description: 'Price changes that are not currently running.',
              adjustments: unscheduledAdjustments,
              emptyMessage: 'No unscheduled price changes.',
            },
          ].map((column) => (
            <section
              key={column.title}
              data-price-change-column={column.key}
              aria-labelledby={`price-change-column-${column.key}`}
              className="space-y-4"
            >
              <div className="flex items-start justify-between gap-3 px-1.5">
                <div className="flex min-w-0 items-start gap-2">
                  <span
                    aria-hidden="true"
                    className={cn(
                      'mt-1.5 size-2 shrink-0 rounded-full',
                      COLUMN_STYLES[column.key].dot
                    )}
                  />
                  <div className="min-w-0 space-y-1">
                    <Typography as="h2" variant="eyebrow" id={`price-change-column-${column.key}`}>
                      {column.title}
                    </Typography>
                    <Typography variant="caption" className="block">
                      {column.description}
                    </Typography>
                  </div>
                </div>
                <Typography variant="label" tone="muted" className="tabular-nums">
                  {getRuleCountLabel(column.adjustments.length)}
                </Typography>
              </div>
              <div data-price-change-column-body={column.key} className="space-y-4">
                {column.adjustments.length > 0 ? (
                  column.adjustments.map((adjustment) => {
                    const isDecrease = adjustment?.operation === 'decrease'
                    const DirectionIcon = isDecrease ? ArrowDown : ArrowUp
                    const activeEffectMeta =
                      adjustment?.status === 'active'
                        ? (activeBoardEffectMetaByAdjustmentId[Number(adjustment.id)] ?? null)
                        : null
                    const stateMeta = getPriceAdjustmentStateMeta(adjustment)
                    const isDormantScheduledCard =
                      column.key === 'scheduled' &&
                      (adjustment?.status === 'disabled' || priceAdjustmentIsSuppressed(adjustment))
                    const isHighlighted = highlightedAdjustmentIds.includes(Number(adjustment.id))

                    let cardSurface = 'default'

                    if (column.key === 'unscheduled') {
                      cardSurface = 'elevated'
                    }

                    if (isDormantScheduledCard && stateMeta?.variant === 'muted') {
                      cardSurface = 'elevated'
                    }

                    if (isDormantScheduledCard && stateMeta?.variant === 'warning') {
                      cardSurface = 'warning'
                    }

                    if (activeEffectMeta?.tone === 'applies') {
                      cardSurface = 'success'
                    }

                    if (isHighlighted) {
                      cardSurface = 'brandSubtle'
                    }

                    return (
                      <Card
                        key={adjustment.id}
                        id={getPriceAdjustmentCardDomId(adjustment.id)}
                        data-price-change-card={adjustment.id}
                        data-active-effect={activeEffectMeta?.tone}
                        data-highlighted={isHighlighted ? 'true' : undefined}
                        className="group relative isolate overflow-hidden"
                        interactive
                        selected={isHighlighted}
                        size="flush"
                        surface={cardSurface}
                      >
                        <CardContent className="space-y-4 p-5">
                          <div className="flex items-start justify-between gap-3">
                            <div className="space-y-1">
                              <Typography variant="cardTitle" interaction="group">
                                {getPriceAdjustmentCardTitle(adjustment)}
                              </Typography>
                              {getPriceAdjustmentSupportingTitle(adjustment) ? (
                                <Typography variant="caption" className="block">
                                  {getPriceAdjustmentSupportingTitle(adjustment)}
                                </Typography>
                              ) : null}
                            </div>
                            <div className="mt-0.5 flex shrink-0 items-center gap-2">
                              {activeEffectMeta ? (
                                <Badge variant={activeEffectMeta.variant} size="sm">
                                  {activeEffectMeta.label}
                                </Badge>
                              ) : null}
                              {priceAdjustmentIsPriority(adjustment) ? (
                                <Badge variant="info" size="sm">
                                  Priority
                                </Badge>
                              ) : null}
                              {stateMeta ? (
                                <Badge variant={stateMeta.variant} size="sm">
                                  {stateMeta.label}
                                </Badge>
                              ) : null}
                              {priceAdjustmentRepeatsAnnually(adjustment) ? (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghostMuted"
                                      size="icon-3xs"
                                      aria-label="Yearly"
                                      className="relative z-10"
                                    >
                                      <Repeat className="size-4" aria-hidden="true" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>Yearly</TooltipContent>
                                </Tooltip>
                              ) : null}
                            </div>
                          </div>

                          <div className="space-y-2.5">
                            <div className="flex items-start justify-between gap-4">
                              <Typography variant="eyebrow" className="shrink-0">
                                Price Change
                              </Typography>
                              <Typography
                                variant="label"
                                align="right"
                                tone={isDecrease ? 'interaction' : 'success'}
                                weight="semibold"
                                className="inline-flex items-center gap-1"
                              >
                                <DirectionIcon className="size-3.5 shrink-0" aria-hidden="true" />
                                {getPriceAdjustmentSignedAmountLabel(adjustment)}
                              </Typography>
                            </div>
                          </div>

                          <div className="flex items-center justify-between gap-4 border-t border-border/30 pt-3">
                            <Typography variant="caption" wrap="pretty">
                              {getPriceAdjustmentScheduleSummary(adjustment)}
                            </Typography>
                            <Typography
                              variant="caption"
                              tone="interaction"
                              interaction="hoverUnderline"
                              className="shrink-0"
                            >
                              Details
                            </Typography>
                          </div>

                          <Link
                            {...path.priceChanges.show(adjustment.id).link}
                            className="absolute inset-0 rounded-[inherit] focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-background"
                          />
                        </CardContent>
                      </Card>
                    )
                  })
                ) : (
                  <EmptyState size="compact" title={column.emptyMessage} />
                )}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  )
}
